import { Configuration } from "api";
import { AxiosResponse } from "axios";
import { ApiStatusException } from "services/apiErrors";

export const baseApiConfig = new Configuration({
  basePath: `${process.env.REACT_APP_API_HOST}/${process.env.REACT_APP_API_ENVIRONMENT}`,
});

export type ApiResponse<T extends { value?: unknown; error?: unknown }> = Pick<
  T,
  "value" | "error"
> & {
  count: number;
  totalRows: number;
};
export type TransformedApiResponse<
  FROM extends { value?: unknown; error?: unknown },
  TO,
> = Pick<FROM, "error"> & {
  count: number;
  totalRows: number;
  value: TO;
};

export const apiResponseTransformer = <
  T extends {
    value?: unknown;
    error?: unknown;
    Count?: number;
    TotalRows?: number;
  },
>(
  result: AxiosResponse<T>
): ApiResponse<T> & { statusCode: number; statusText: string } => ({
  value: result.data.value,
  error: result.data.error,
  count: result.data.Count ?? 0,
  totalRows: result.data.TotalRows ?? 0,
  statusCode: result.status,
  statusText: result.statusText,
});

export type PaginatedApiResponse<
  T extends { value?: unknown; error?: unknown },
> = Pick<T, "value" | "error"> & {
  count: number;
  totalRows: number;
} & Pagination;

export const PER_PAGE = 15;

export const paginatedApiResponseTransformer =
  (page: number | undefined) =>
  <
    T extends {
      value?: unknown;
      error?: unknown;
      Count?: number;
      TotalRows?: number;
    },
  >(
    result: AxiosResponse<T>
  ): PaginatedApiResponse<T> & {
    statusCode: number;
    statusText: string;
  } & Pagination => ({
    value: result.data.value,
    error: result.data.error,
    count: result.data.Count ?? 0,
    totalRows: result.data.TotalRows ?? 0,
    statusCode: result.status,
    statusText: result.statusText,
    page: page ?? 1,
    totalPages: result.data.TotalRows
      ? Math.ceil(result.data.TotalRows / PER_PAGE)
      : 0,
  });

export const filterOnlyUnique = <T>(
  value: T,
  index: number,
  self: T[]
): boolean => self.indexOf(value) === index;

export const filterOnlyUniqueByKey =
  (key: string) =>
  <T extends Record<string, any>>(
    value: T,
    index: number,
    self: T[]
  ): boolean =>
    self.findIndex((other) => other[key] === value[key]) === index;

export const removeNullable = <T>(arr: T[]): NonNullable<T>[] =>
  (arr?.filter((value) => !!value) as NonNullable<T>[]) ?? [];

export const invalidStatusCode = (statusCode: number): boolean =>
  statusCode < 200 || statusCode >= 500;

export const andFilters = (filters: (string | null)[]): string =>
  removeNullable(filters)
    .filter((filter) => filter.length > 0)
    .join(` and `);

export const orFilters = (filters: (string | null)[]): string =>
  filters.length > 0
    ? `(${removeNullable(filters)
        .filter((filter) => filter.length > 0)
        .join(" or ")})`
    : "";

export const listOfFilters = (filters: (string | null)[]): string =>
  filters.length > 0
    ? `('${removeNullable(filters)
        .filter((filter) => filter.length > 0)
        .join("','")}')`
    : "()";

export const sortToString = (sort?: SortParameter): string | undefined =>
  !!sort?.key && !!sort?.direction
    ? `${sort.key} ${sort.direction}`
    : undefined;

export const validateApiResponse =
  (errorMsg = "An error occurred") =>
  <T extends { statusCode: number; statusText: string }>(data: T) => {
    if (!data) {
      throw new Error(errorMsg);
    } else if (invalidStatusCode(data.statusCode)) {
      throw new ApiStatusException(data.statusText, data.statusCode);
    }
    return data;
  };

export const formatDateForApi = (data: Date) =>
  data.toISOString().split("T")[0] ?? ""; // Only use date portion
